/**
 * SmartChat Widget - 企業級智能客服嵌入套件
 *
 * @version 1.0.0
 * @author SmartChat Team
 * @copyright 2024 SmartChat Corporation. All rights reserved.
 * @license Proprietary - 此程式碼受著作權保護，禁止未經授權的複製、修改或分發
 *
 * 警告：本軟體包含專有技術和商業機密。
 * 未經明確書面許可，嚴禁逆向工程、反編譯或以任何方式複製此程式碼。
 * 違反者將面臨法律後果，包括但不限於損害賠償和刑事起訴。
 *
 * 如需合法使用授權，請聯絡：legal@smartchat.com
 */
export class SmartChatWidget {
    constructor(config) {
        this.container = null;
        this.widgetElement = null;
        this.isOpen = false;
        this.isMinimized = true;
        this.currentSession = null;
        this.eventHandlers = {};
        this.clientToken = null;
        this.config = {
            apiEndpoint: 'https://api.smartchat.com/api',
            theme: {
                primaryColor: '#007bff',
                secondaryColor: '#6c757d',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                position: 'bottom-right'
            },
            ui: {
                showHeader: true,
                showAvatar: true,
                placeholder: '輸入您的訊息...',
                welcomeMessage: '您好！我是智能助理，請問有什麼可以幫助您的嗎？'
            },
            features: {
                fileUpload: false,
                quickReplies: true,
                typing: true
            },
            debug: false,
            ...config
        };
        this.apiEndpoint = this.config.apiEndpoint;
        if (this.config.debug) {
            console.log('SmartChat Widget initialized with config:', this.config);
            this.showVersionInfo();
        }
        // 自動初始化
        this.init();
    }
    /**
     * 初始化 Widget
     */
    async init() {
        try {
            // 驗證必要參數
            if (!this.config.clientId) {
                throw new Error('clientId is required');
            }
            // 獲取客戶端令牌
            await this.authenticate();
            // 創建 Widget UI
            this.createWidget();
            // 設置事件監聽
            this.setupEventListeners();
            this.emit('initialized', { config: this.config });
            if (this.config.debug) {
                console.log('SmartChat Widget initialized successfully');
            }
        }
        catch (error) {
            console.error('SmartChat Widget initialization failed:', error);
            this.emit('error', { error });
        }
    }
    /**
     * 客戶端認證
     */
    async authenticate() {
        try {
            const response = await fetch(`${this.apiEndpoint}/auth/client-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Domain': window.location.hostname
                },
                body: JSON.stringify({
                    clientId: this.config.clientId,
                    domain: window.location.hostname,
                    timestamp: Math.floor(Date.now() / 1000),
                    nonce: this.generateNonce()
                })
            });
            if (!response.ok) {
                // 開發模式下使用模擬令牌
                if (this.config.debug) {
                    this.clientToken = 'mock-token-' + Date.now();
                    return;
                }
                throw new Error(`Authentication failed: ${response.status}`);
            }
            const data = await response.json();
            this.clientToken = data.clientToken || data.accessToken;
        }
        catch (error) {
            if (this.config.debug) {
                console.warn('Using mock authentication for development');
                this.clientToken = 'mock-token-' + Date.now();
            }
            else {
                throw error;
            }
        }
    }
    /**
     * 創建 Widget UI
     */
    createWidget() {
        // 創建容器
        this.container = document.createElement('div');
        this.container.id = 'smartchat-widget-container';
        this.container.className = 'smartchat-widget-container';
        // 設置容器樣式
        this.applyContainerStyles();
        // 創建 Widget 元素
        this.widgetElement = document.createElement('div');
        this.widgetElement.className = 'smartchat-widget';
        this.widgetElement.innerHTML = this.getWidgetHTML();
        // 添加樣式
        this.injectStyles();
        // 組裝
        this.container.appendChild(this.widgetElement);
        document.body.appendChild(this.container);
        // 初始狀態為最小化
        this.updateMinimizedState();
    }
    /**
     * 獲取 Widget HTML
     */
    getWidgetHTML() {
        var _a, _b, _c, _d;
        return `
      <!-- 最小化按鈕 -->
      <div class="smartchat-trigger" id="smartchat-trigger" title="SmartChat Widget v${SmartChatWidget.VERSION} - 點擊開啟智能客服">
        <div class="smartchat-trigger-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16 13H8V11H16V13Z" fill="currentColor"/>
          </svg>
        </div>
        <div class="smartchat-trigger-text">💬</div>
      </div>

      <!-- 聊天窗口 -->
      <div class="smartchat-window" id="smartchat-window">
        <!-- 標題列 -->
        ${((_a = this.config.ui) === null || _a === void 0 ? void 0 : _a.showHeader) ? `
        <div class="smartchat-header">
          <div class="smartchat-bot-info">
            ${((_b = this.config.ui) === null || _b === void 0 ? void 0 : _b.showAvatar) ? '<div class="smartchat-avatar">🤖</div>' : ''}
            <div class="smartchat-bot-details">
              <div class="smartchat-bot-name">SmartChat 助理</div>
              <div class="smartchat-bot-status" id="smartchat-status">線上服務 v${SmartChatWidget.VERSION}</div>
            </div>
          </div>
          <div class="smartchat-header-actions">
            <button class="smartchat-btn smartchat-btn-minimize" id="smartchat-minimize">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 8h8" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
        </div>
        ` : ''}

        <!-- 訊息區域 -->
        <div class="smartchat-messages" id="smartchat-messages">
          <div class="smartchat-welcome-message">
            ${((_c = this.config.ui) === null || _c === void 0 ? void 0 : _c.welcomeMessage) || '您好！我是智能助理，請問有什麼可以幫助您的嗎？'}
          </div>
        </div>

        <!-- 快速回覆 -->
        <div class="smartchat-quick-replies" id="smartchat-quick-replies" style="display: none;"></div>

        <!-- 輸入區域 -->
        <div class="smartchat-input-area">
          <div class="smartchat-input-group">
            <input type="text" 
                   class="smartchat-input" 
                   id="smartchat-input"
                   placeholder="${((_d = this.config.ui) === null || _d === void 0 ? void 0 : _d.placeholder) || '輸入您的訊息...'}"
                   maxlength="500">
            <button class="smartchat-btn smartchat-btn-send" id="smartchat-send" disabled>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 2L7 9M14 2L10 14L7 9M14 2L2 6L7 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
    }
    /**
     * 設置事件監聽器
     */
    setupEventListeners() {
        if (!this.widgetElement)
            return;
        // 觸發按鈕
        const trigger = this.widgetElement.querySelector('#smartchat-trigger');
        trigger === null || trigger === void 0 ? void 0 : trigger.addEventListener('click', () => this.toggle());
        // 最小化按鈕
        const minimizeBtn = this.widgetElement.querySelector('#smartchat-minimize');
        minimizeBtn === null || minimizeBtn === void 0 ? void 0 : minimizeBtn.addEventListener('click', () => this.minimize());
        // 輸入框和發送按鈕
        const input = this.widgetElement.querySelector('#smartchat-input');
        const sendBtn = this.widgetElement.querySelector('#smartchat-send');
        input === null || input === void 0 ? void 0 : input.addEventListener('input', (e) => {
            const target = e.target;
            sendBtn.disabled = !target.value.trim();
        });
        input === null || input === void 0 ? void 0 : input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendMessage();
            }
        });
        sendBtn === null || sendBtn === void 0 ? void 0 : sendBtn.addEventListener('click', () => this.sendMessage());
        // 點擊外部關閉 (可選)
        document.addEventListener('click', (e) => {
            var _a;
            if (this.isOpen && !((_a = this.container) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                // 可以選擇是否點擊外部關閉
                // this.minimize();
            }
        });
    }
    /**
     * 發送訊息
     */
    async sendMessage(content) {
        var _a, _b;
        const input = (_a = this.widgetElement) === null || _a === void 0 ? void 0 : _a.querySelector('#smartchat-input');
        const message = content || (input === null || input === void 0 ? void 0 : input.value.trim());
        if (!message)
            return;
        // 清空輸入框
        if (input) {
            input.value = '';
            ((_b = this.widgetElement) === null || _b === void 0 ? void 0 : _b.querySelector('#smartchat-send')).disabled = true;
        }
        // 顯示用戶訊息
        this.addMessage({
            id: this.generateId(),
            content: message,
            sender: 'user',
            timestamp: new Date().toISOString()
        });
        // 顯示輸入中狀態
        this.setTypingStatus(true);
        try {
            // 如果沒有會話，先創建會話
            if (!this.currentSession) {
                await this.startSession();
            }
            // 發送訊息到 API
            const response = await this.sendMessageToAPI(message);
            // 顯示機器人回應
            this.addMessage({
                id: response.messageId || this.generateId(),
                content: response.content,
                sender: 'bot',
                timestamp: new Date().toISOString(),
                quickReplies: response.quickReplies
            });
            // 顯示快速回覆
            if (response.quickReplies && response.quickReplies.length > 0) {
                this.showQuickReplies(response.quickReplies);
            }
        }
        catch (error) {
            console.error('Failed to send message:', error);
            this.addMessage({
                id: this.generateId(),
                content: '抱歉，發生了錯誤，請稍後再試。',
                sender: 'bot',
                timestamp: new Date().toISOString()
            });
        }
        finally {
            this.setTypingStatus(false);
        }
    }
    /**
     * 開始聊天會話
     */
    async startSession() {
        try {
            const sessionData = {
                sessionId: this.generateId(),
                messages: [],
                isActive: true
            };
            this.currentSession = sessionData;
            this.emit('session-started', { sessionId: sessionData.sessionId });
        }
        catch (error) {
            console.error('Failed to start session:', error);
            throw error;
        }
    }
    /**
     * 發送訊息到 API
     */
    async sendMessageToAPI(message) {
        var _a;
        // 在開發模式下使用模擬回應
        if (this.config.debug || !this.clientToken || this.clientToken.startsWith('mock-')) {
            return this.getMockResponse(message);
        }
        try {
            const response = await fetch(`${this.apiEndpoint}/chat/sessions/${(_a = this.currentSession) === null || _a === void 0 ? void 0 : _a.sessionId}/messages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.clientToken}`,
                    'X-Client-ID': this.config.clientId
                },
                body: JSON.stringify({
                    content: message,
                    type: 'text'
                })
            });
            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }
            const data = await response.json();
            return data.success ? data.data : data;
        }
        catch (error) {
            // 降級到模擬回應
            if (this.config.debug) {
                console.warn('API request failed, using mock response:', error);
                return this.getMockResponse(message);
            }
            throw error;
        }
    }
    /**
     * 獲取模擬回應
     */
    getMockResponse(message) {
        const responses = [
            {
                content: '謝謝您的提問！我理解您的需求，讓我為您提供協助。',
                quickReplies: [
                    { id: '1', text: '了解更多', value: 'more_info' },
                    { id: '2', text: '聯絡客服', value: 'contact' }
                ]
            },
            {
                content: '這是一個很好的問題！根據您的情況，我建議您可以考慮以下方案。',
                quickReplies: [
                    { id: '1', text: '方案A', value: 'option_a' },
                    { id: '2', text: '方案B', value: 'option_b' }
                ]
            },
            {
                content: '我很樂意為您解答！請問您還有其他問題嗎？',
                quickReplies: [
                    { id: '1', text: '是的', value: 'yes' },
                    { id: '2', text: '沒有了', value: 'no' }
                ]
            }
        ];
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        return new Promise(resolve => {
            setTimeout(() => resolve({
                messageId: this.generateId(),
                ...randomResponse
            }), 1000 + Math.random() * 1000);
        });
    }
    /**
     * 添加訊息到聊天區域
     */
    addMessage(message) {
        var _a;
        const messagesContainer = (_a = this.widgetElement) === null || _a === void 0 ? void 0 : _a.querySelector('#smartchat-messages');
        if (!messagesContainer)
            return;
        const messageElement = document.createElement('div');
        messageElement.className = `smartchat-message smartchat-message-${message.sender}`;
        messageElement.innerHTML = `
      <div class="smartchat-message-content">
        <div class="smartchat-message-text">${this.escapeHtml(message.content)}</div>
        <div class="smartchat-message-time">${this.formatTime(message.timestamp)}</div>
      </div>
    `;
        messagesContainer.appendChild(messageElement);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        // 更新會話
        if (this.currentSession) {
            this.currentSession.messages.push(message);
        }
        this.emit('message-added', { message });
    }
    /**
     * 顯示快速回覆
     */
    showQuickReplies(replies) {
        var _a;
        const repliesContainer = (_a = this.widgetElement) === null || _a === void 0 ? void 0 : _a.querySelector('#smartchat-quick-replies');
        if (!repliesContainer)
            return;
        repliesContainer.innerHTML = replies.map(reply => `<button class="smartchat-quick-reply" data-value="${reply.value}">${this.escapeHtml(reply.text)}</button>`).join('');
        repliesContainer.style.display = 'block';
        // 設置點擊事件
        repliesContainer.addEventListener('click', (e) => {
            const target = e.target;
            if (target.classList.contains('smartchat-quick-reply')) {
                const value = target.dataset.value;
                const text = target.textContent;
                if (text) {
                    this.sendMessage(text);
                    repliesContainer.style.display = 'none';
                }
            }
        });
    }
    /**
     * 設置輸入中狀態
     */
    setTypingStatus(isTyping) {
        var _a;
        const status = (_a = this.widgetElement) === null || _a === void 0 ? void 0 : _a.querySelector('#smartchat-status');
        if (status) {
            status.textContent = isTyping ? '正在輸入...' : `線上服務 v${SmartChatWidget.VERSION}`;
        }
        if (isTyping) {
            // 可以添加輸入中動畫
        }
    }
    // =============== 公開 API ===============
    /**
     * 打開 Widget
     */
    open() {
        if (this.isMinimized) {
            this.isMinimized = false;
            this.isOpen = true;
            this.updateMinimizedState();
            this.emit('opened');
        }
    }
    /**
     * 關閉 Widget
     */
    close() {
        if (!this.isMinimized) {
            this.minimize();
        }
    }
    /**
     * 最小化 Widget
     */
    minimize() {
        this.isMinimized = true;
        this.isOpen = false;
        this.updateMinimizedState();
        this.emit('minimized');
    }
    /**
     * 切換 Widget 狀態
     */
    toggle() {
        if (this.isMinimized) {
            this.open();
        }
        else {
            this.minimize();
        }
    }
    /**
     * 發送訊息 (公開API)
     */
    send(message) {
        return this.sendMessage(message);
    }
    /**
     * 清空聊天記錄
     */
    clearChat() {
        var _a, _b;
        const messagesContainer = (_a = this.widgetElement) === null || _a === void 0 ? void 0 : _a.querySelector('#smartchat-messages');
        if (messagesContainer) {
            messagesContainer.innerHTML = `
        <div class="smartchat-welcome-message">
          ${((_b = this.config.ui) === null || _b === void 0 ? void 0 : _b.welcomeMessage) || '您好！我是智能助理，請問有什麼可以幫助您的嗎？'}
        </div>
      `;
        }
        if (this.currentSession) {
            this.currentSession.messages = [];
        }
        this.emit('chat-cleared');
    }
    /**
     * 銷毀 Widget
     */
    destroy() {
        if (this.container) {
            this.container.remove();
        }
        this.emit('destroyed');
    }
    /**
     * 獲取版本資訊
     */
    getVersion() {
        return SmartChatWidget.VERSION;
    }
    /**
     * 獲取完整版本資訊
     */
    getVersionInfo() {
        return {
            version: SmartChatWidget.VERSION,
            buildDate: SmartChatWidget.BUILD_DATE,
            name: 'SmartChat Widget'
        };
    }
    /**
     * 顯示版本資訊到控制台
     */
    showVersionInfo() {
        const info = this.getVersionInfo();
        console.log(`%c${info.name} v${info.version}`, 'background: #007bff; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold');
        console.log(`Build Date: ${info.buildDate}`);
    }
    /**
     * 事件監聽
     */
    on(event, handler) {
        if (!this.eventHandlers[event]) {
            this.eventHandlers[event] = [];
        }
        this.eventHandlers[event].push(handler);
    }
    /**
     * 移除事件監聽
     */
    off(event, handler) {
        if (!this.eventHandlers[event])
            return;
        if (handler) {
            const index = this.eventHandlers[event].indexOf(handler);
            if (index > -1) {
                this.eventHandlers[event].splice(index, 1);
            }
        }
        else {
            this.eventHandlers[event] = [];
        }
    }
    // =============== 私有工具方法 ===============
    emit(event, data) {
        if (this.eventHandlers[event]) {
            this.eventHandlers[event].forEach(handler => {
                try {
                    handler(data);
                }
                catch (error) {
                    console.error('Event handler error:', error);
                }
            });
        }
        if (this.config.debug) {
            console.log('SmartChat Event:', event, data);
        }
    }
    updateMinimizedState() {
        if (!this.container)
            return;
        const trigger = this.container.querySelector('#smartchat-trigger');
        const window = this.container.querySelector('#smartchat-window');
        if (this.isMinimized) {
            trigger.style.display = 'flex';
            window.style.display = 'none';
            this.container.classList.remove('smartchat-open');
        }
        else {
            trigger.style.display = 'none';
            window.style.display = 'flex';
            this.container.classList.add('smartchat-open');
        }
    }
    applyContainerStyles() {
        var _a, _b;
        if (!this.container)
            return;
        const position = ((_a = this.config.theme) === null || _a === void 0 ? void 0 : _a.position) || 'bottom-right';
        const positionStyles = this.getPositionStyles(position);
        Object.assign(this.container.style, {
            position: 'fixed',
            zIndex: '999999',
            fontFamily: (_b = this.config.theme) === null || _b === void 0 ? void 0 : _b.fontFamily,
            ...positionStyles
        });
    }
    getPositionStyles(position) {
        const offset = '20px';
        switch (position) {
            case 'bottom-right':
                return { bottom: offset, right: offset };
            case 'bottom-left':
                return { bottom: offset, left: offset };
            case 'top-right':
                return { top: offset, right: offset };
            case 'top-left':
                return { top: offset, left: offset };
            case 'center':
                return {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                };
            default:
                return { bottom: offset, right: offset };
        }
    }
    injectStyles() {
        if (document.getElementById('smartchat-widget-styles'))
            return;
        const style = document.createElement('style');
        style.id = 'smartchat-widget-styles';
        style.textContent = this.getWidgetCSS();
        document.head.appendChild(style);
    }
    getWidgetCSS() {
        var _a, _b, _c;
        const primaryColor = ((_a = this.config.theme) === null || _a === void 0 ? void 0 : _a.primaryColor) || '#007bff';
        const secondaryColor = ((_b = this.config.theme) === null || _b === void 0 ? void 0 : _b.secondaryColor) || '#6c757d';
        return `
      /* SmartChat Widget Styles */
      .smartchat-widget-container {
        --primary-color: ${primaryColor};
        --secondary-color: ${secondaryColor};
        font-family: ${((_c = this.config.theme) === null || _c === void 0 ? void 0 : _c.fontFamily) || 'system-ui'};
      }
      
      .smartchat-trigger {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: var(--primary-color);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transition: all 0.3s ease;
      }
      
      .smartchat-trigger:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 20px rgba(0,0,0,0.2);
      }
      
      .smartchat-window {
        width: 350px;
        height: 500px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.15);
        display: none;
        flex-direction: column;
        overflow: hidden;
      }
      
      .smartchat-header {
        background: var(--primary-color);
        color: white;
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      
      .smartchat-bot-info {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      
      .smartchat-avatar {
        font-size: 24px;
      }
      
      .smartchat-bot-name {
        font-weight: 600;
        font-size: 16px;
      }
      
      .smartchat-bot-status {
        font-size: 12px;
        opacity: 0.8;
      }
      
      .smartchat-messages {
        flex: 1;
        padding: 16px;
        overflow-y: auto;
        background: #f8f9fa;
      }
      
      .smartchat-welcome-message {
        background: white;
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 16px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      .smartchat-message {
        margin-bottom: 12px;
      }
      
      .smartchat-message-user .smartchat-message-content {
        background: var(--primary-color);
        color: white;
        margin-left: 50px;
      }
      
      .smartchat-message-bot .smartchat-message-content {
        background: white;
        margin-right: 50px;
      }
      
      .smartchat-message-content {
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      .smartchat-message-time {
        font-size: 11px;
        opacity: 0.6;
        margin-top: 4px;
      }
      
      .smartchat-quick-replies {
        padding: 0 16px 8px;
        display: none;
      }
      
      .smartchat-quick-reply {
        background: white;
        border: 1px solid var(--primary-color);
        color: var(--primary-color);
        padding: 8px 16px;
        border-radius: 20px;
        margin: 4px 4px 4px 0;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s ease;
      }
      
      .smartchat-quick-reply:hover {
        background: var(--primary-color);
        color: white;
      }
      
      .smartchat-input-area {
        border-top: 1px solid #e9ecef;
        padding: 16px;
      }
      
      .smartchat-input-group {
        display: flex;
        gap: 8px;
      }
      
      .smartchat-input {
        flex: 1;
        border: 1px solid #ddd;
        border-radius: 20px;
        padding: 12px 16px;
        outline: none;
        font-size: 14px;
      }
      
      .smartchat-input:focus {
        border-color: var(--primary-color);
      }
      
      .smartchat-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      }
      
      .smartchat-btn-send {
        background: var(--primary-color);
        color: white;
        width: 40px;
        height: 40px;
      }
      
      .smartchat-btn-send:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
      
      .smartchat-btn-minimize {
        color: white;
        opacity: 0.7;
      }
      
      .smartchat-btn:hover {
        opacity: 0.8;
      }
      
      /* 響應式設計 */
      @media (max-width: 480px) {
        .smartchat-window {
          width: calc(100vw - 40px);
          height: calc(100vh - 40px);
          max-width: none;
          max-height: none;
        }
      }
    `;
    }
    generateId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
    generateNonce() {
        return Math.random().toString(36).substr(2, 15);
    }
    formatTime(timestamp) {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('zh-TW', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
// 版本資訊
SmartChatWidget.VERSION = '1.0.11';
SmartChatWidget.BUILD_DATE = '2025-09-03';
// 自動初始化
if (typeof window !== 'undefined') {
    window.SmartChatWidget = SmartChatWidget;
    // 如果有全域配置，自動創建實例
    if (window.SmartChatConfig) {
        new SmartChatWidget(window.SmartChatConfig);
    }
}
export default SmartChatWidget;
